/**
 * RETAIL PRODUCT FINDER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * WidgetConfigTab — appearance and behaviour of the public store finder, plus
 * the copy-paste embed snippet. Saved to `retail_widget_config` and read by the
 * `retail-finder-public` function at page load, so changes here go live on the
 * customer's website without anyone redeploying it.
 *
 * PORTED from the source app's WidgetConfigTab. It was deferred at port time on
 * the grounds that "it configures a widget that doesn't exist in Brand Command
 * yet" — true then, obsolete now that `embed/dist/retail-finder.js` ships.
 *
 * Two things are NEW here, both consequences of being multi-tenant:
 *   • the embed key — the source identified itself with a brand string; here the
 *     snippet carries `retail_widget_config.public_key`, which is how the public
 *     endpoint resolves which tenant is asking;
 *   • the ALLOWED WEBSITES list — the source's allowlist was an environment
 *     variable on the edge function. Here it is per tenant and **empty means the
 *     widget is switched off**, which is the correct default but is also the
 *     single most likely reason someone's widget "doesn't work". It is therefore
 *     the first card on the tab, not a footnote.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  loadWidgetSettings, saveWidgetSettings, widgetSnippet,
} from "../services/retailLocationService.js";

const BASEMAPS = {
  positron: { label: "Clean light (recommended)", tileUrl: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" },
  voyager:  { label: "Soft colour",               tileUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" },
  osm:      { label: "OpenStreetMap",             tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" },
  dark:     { label: "Dark",                      tileUrl: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" },
};

export const DEFAULT_WIDGET_CONFIG = {
  map: {
    basemapStyle: "positron",
    tileUrl: BASEMAPS.positron.tileUrl,
    tileFilter: "",
    defaultCenter: { lat: 39.5, lng: -98.35 },
    defaultZoom: 4,
    clustering: true,
  },
  search: {
    radiusOptions: [5, 10, 25, 50, 100],
    maxRadius: 100,
    defaultRadius: 25,
    units: "mi",
    autocomplete: true,
    geolocationButton: true,
    placeholder: "Enter your ZIP or City, State",
  },
  results: { limit: 100, listView: true, showDistance: true },
  filters: { byProduct: true, filterableProducts: [] },
  appearance: {
    inheritSiteCss: false,
    marker: { iconUrl: "", color: "#E23A2E", selectedColor: "#B71C1C", clusterColor: "#E23A2E" },
    font: { family: "system-ui, -apple-system, Segoe UI, sans-serif", url: "" },
    colors: {
      primary: "#E23A2E", text: "#1A1A1A", muted: "#6B7280",
      background: "#FFFFFF", border: "#E5E7EB", accent: "#E23A2E",
    },
    cornerRadius: 10,
    cardFields: ["name", "address", "distance", "products", "directions"],
    customCss: "",
  },
  messaging: {
    noResults: "No locations within {radius} miles — try a wider search.",
    cta: { label: "", url: "" },
  },
};

// Merge stored config over the defaults so a partially-saved config still works.
function withDefaults(base, stored) {
  if (Array.isArray(base)) return stored ?? base;
  if (base && typeof base === "object") {
    const out = { ...base };
    for (const k of Object.keys(base)) out[k] = withDefaults(base[k], stored?.[k]);
    for (const k of Object.keys(stored || {})) if (!(k in out)) out[k] = stored[k];
    return out;
  }
  return stored === undefined ? base : stored;
}

export default function WidgetConfigTab({ canEdit = true }) {
  const [cfg, setCfg] = useState(DEFAULT_WIDGET_CONFIG);
  const [publicKey, setPublicKey] = useState(null);
  const [origins, setOrigins] = useState("");
  const [savedOrigins, setSavedOrigins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await loadWidgetSettings();
      setCfg(withDefaults(DEFAULT_WIDGET_CONFIG, s.config));
      setPublicKey(s.publicKey);
      setSavedOrigins(s.allowedOrigins);
      setOrigins(s.allowedOrigins.join("\n"));
    } catch (e) {
      setError(e.message || "Failed to load the widget configuration.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (path, value) => setCfg((prev) => {
    const next = structuredClone(prev);
    const keys = path.split(".");
    let node = next;
    for (let i = 0; i < keys.length - 1; i++) node = node[keys[i]];
    node[keys[keys.length - 1]] = value;
    return next;
  });

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      // Keep tileUrl consistent with the chosen basemap.
      const config = structuredClone(cfg);
      config.map.tileUrl = BASEMAPS[config.map.basemapStyle]?.tileUrl || config.map.tileUrl;
      const list = origins.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      const next = await saveWidgetSettings({ config, allowedOrigins: list });
      setPublicKey(next.publicKey);
      setSavedOrigins(next.allowedOrigins);
      setOrigins(next.allowedOrigins.join("\n"));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message || "Failed to save the widget configuration.");
    } finally {
      setSaving(false);
    }
  };

  const embed = publicKey
    ? widgetSnippet(publicKey)
    : "Save once to generate your embed code.";

  const copyEmbed = async () => {
    if (!publicKey) return;
    try {
      await navigator.clipboard.writeText(embed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — the user can select the text */ }
  };

  if (loading) return <div className="rpf-note">Loading…</div>;

  const a = cfg.appearance;
  const disabled = !canEdit;
  const live = savedOrigins.length > 0;
  // Applied to exactly the fields `inheritSiteCss` disables, so "looks disabled"
  // and "is disabled" can't drift apart again.
  const inheritDim = a.inheritSiteCss ? { opacity: 0.5 } : undefined;

  return (
    <div className="rpf-widget-config">
      {error && <div className="rpf-alert">{error}</div>}
      {!canEdit && (
        <div className="rpf-note">
          You have view-only access. Workspace owner or admin rights are required to
          change how the website widget looks or behaves.
        </div>
      )}

      {/* First, because an empty allowlist is the single most likely reason a
          widget "doesn't work", and it is the correct default. */}
      <section className="rpf-card">
        <h3>
          Allowed websites{" "}
          <span className={`rpf-badge ${live ? "rpf-badge-on" : "rpf-badge-warn"}`}>
            {live ? "Live" : "Switched off"}
          </span>
        </h3>
        <p className="rpf-sub">
          The store finder only answers pages on these websites. <strong>Until you add
          one, it stays switched off</strong> — that is deliberate, so a store list
          can’t be embedded anywhere until its owner says where.
        </p>
        <label className="rpf-field rpf-field-wide">
          <span>One website per line — scheme and domain only, e.g. https://yourbrand.com</span>
          <textarea
            rows={3}
            value={origins}
            disabled={disabled}
            placeholder="https://yourbrand.com"
            onChange={(e) => setOrigins(e.target.value)}
          />
        </label>
        <p className="rpf-sub">
          Add <code>http://localhost:5173</code> too if you want to try it locally.
          A trailing path or slash never matches and will be trimmed on save.
        </p>
      </section>

      <section className="rpf-card">
        <h3>Map</h3>
        <div className="rpf-map-grid">
          <label className="rpf-field">
            <span>Basemap style</span>
            <select value={cfg.map.basemapStyle} disabled={disabled}
                    onChange={(e) => set("map.basemapStyle", e.target.value)}>
              {Object.entries(BASEMAPS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
          <label className="rpf-field">
            <span>Tint (CSS filter, optional)</span>
            <input value={cfg.map.tileFilter} disabled={disabled} placeholder="e.g. grayscale(1)"
                   onChange={(e) => set("map.tileFilter", e.target.value)} />
          </label>
          <label className="rpf-field">
            <span>Default zoom</span>
            <input type="number" min="1" max="18" value={cfg.map.defaultZoom} disabled={disabled}
                   onChange={(e) => set("map.defaultZoom", Number(e.target.value))} />
          </label>
          <label className="rpf-check">
            <input type="checkbox" checked={cfg.map.clustering} disabled={disabled}
                   onChange={(e) => set("map.clustering", e.target.checked)} />
            Group nearby pins into clusters
          </label>
        </div>
      </section>

      <section className="rpf-card">
        <h3>Search &amp; results</h3>
        <div className="rpf-map-grid">
          <label className="rpf-field">
            <span>Maximum radius (miles)</span>
            <input type="number" min="1" max="250" value={cfg.search.maxRadius} disabled={disabled}
                   onChange={(e) => set("search.maxRadius", Number(e.target.value))} />
          </label>
          <label className="rpf-field">
            <span>Default radius (miles)</span>
            <input type="number" min="1" max="250" value={cfg.search.defaultRadius} disabled={disabled}
                   onChange={(e) => set("search.defaultRadius", Number(e.target.value))} />
          </label>
          <label className="rpf-field">
            <span>Maximum results</span>
            <input type="number" min="1" max="250" value={cfg.results.limit} disabled={disabled}
                   onChange={(e) => set("results.limit", Number(e.target.value))} />
          </label>
          <label className="rpf-field">
            <span>Search box placeholder</span>
            <input value={cfg.search.placeholder} disabled={disabled}
                   onChange={(e) => set("search.placeholder", e.target.value)} />
          </label>
          <label className="rpf-check">
            <input type="checkbox" checked={cfg.search.geolocationButton} disabled={disabled}
                   onChange={(e) => set("search.geolocationButton", e.target.checked)} />
            Show the “Use my location” button
          </label>
          <label className="rpf-check">
            <input type="checkbox" checked={cfg.search.autocomplete} disabled={disabled}
                   onChange={(e) => set("search.autocomplete", e.target.checked)} />
            Suggest ZIPs and cities as the visitor types
          </label>
          <label className="rpf-check">
            <input type="checkbox" checked={cfg.filters.byProduct} disabled={disabled}
                   onChange={(e) => set("filters.byProduct", e.target.checked)} />
            Let visitors filter by product
          </label>
        </div>
        <p className="rpf-sub">
          Both caps are enforced on the server, so a visitor can never request a wider
          radius or more results than set here.
        </p>
      </section>

      <section className="rpf-card">
        <h3>Appearance</h3>
        <label className="rpf-check">
          <input type="checkbox" checked={a.inheritSiteCss} disabled={disabled}
                 onChange={(e) => set("appearance.inheritSiteCss", e.target.checked)} />
          Inherit the website’s fonts and text colour (the widget blends into the page)
        </label>
        {/* Backlog item 75: this card was eight controls in one flat column, six
            of which most tenants never touch. The three colours are the whole
            job for nearly everyone; the rest is behind a disclosure. Nothing was
            removed or renamed — a tenant who set a font stack still finds it,
            one click further in. */}
        {/* The dimming is per-field, not on the grid. It used to sit on the grid,
            which dimmed Pin colour too — but "inherit the site's fonts and text
            colour" never disabled Pin colour, so the one control that stayed
            editable was the one that looked switched off. Whether pin colour
            SHOULD follow the site is a product question and is not answered
            here; this only stops the UI misreporting what it already does. */}
        <div className="rpf-map-grid">
          <label className="rpf-field" style={inheritDim}>
            <span>Primary colour</span>
            <input type="color" value={a.colors.primary} disabled={disabled || a.inheritSiteCss}
                   onChange={(e) => set("appearance.colors.primary", e.target.value)} />
          </label>
          <label className="rpf-field" style={inheritDim}>
            <span>Text colour</span>
            <input type="color" value={a.colors.text} disabled={disabled || a.inheritSiteCss}
                   onChange={(e) => set("appearance.colors.text", e.target.value)} />
          </label>
          <label className="rpf-field">
            <span>Pin colour</span>
            <input type="color" value={a.marker.color} disabled={disabled}
                   onChange={(e) => set("appearance.marker.color", e.target.value)} />
          </label>
        </div>

        <details className="rpf-more">
          <summary>Fine-tuning — pin image, font, corner radius, custom CSS</summary>
          <div className="rpf-map-grid">
            <label className="rpf-field">
              <span>Corner radius (px)</span>
              <input type="number" min="0" max="32" value={a.cornerRadius} disabled={disabled}
                     onChange={(e) => set("appearance.cornerRadius", Number(e.target.value))} />
            </label>
            <label className="rpf-field">
              <span>Custom pin image URL <em>(optional)</em></span>
              <input value={a.marker.iconUrl} disabled={disabled}
                     onChange={(e) => set("appearance.marker.iconUrl", e.target.value)} />
            </label>
            {/* Same per-field dimming rule as the colours above — this is the
                only control in the disclosure that "inherit" disables. */}
            <label className="rpf-field" style={inheritDim}>
              <span>Font stack</span>
              <input value={a.font.family} disabled={disabled || a.inheritSiteCss}
                     onChange={(e) => set("appearance.font.family", e.target.value)} />
            </label>
          </div>
          <label className="rpf-field">
            <span>Custom CSS <em>(applied last, inside the widget only)</em></span>
            <textarea rows={4} value={a.customCss} disabled={disabled}
                      onChange={(e) => set("appearance.customCss", e.target.value)} />
          </label>
        </details>
      </section>

      {/* Collapsed by default (item 75): empty-state text and an optional CTA
          both ship with working defaults, so nobody has to open this to go live.
          The <h3> stays INSIDE the <summary> — a <summary> is not a heading, and
          dropping it would take this tab's heading outline from six to five. */}
      <section className="rpf-card">
        <details className="rpf-more rpf-more-card">
          <summary><h3>Messaging</h3></summary>
          <div className="rpf-map-grid">
            <label className="rpf-field rpf-field-wide">
              <span>Shown when nothing is found ({"{radius}"} is replaced with the miles searched)</span>
              <input value={cfg.messaging.noResults} disabled={disabled}
                     onChange={(e) => set("messaging.noResults", e.target.value)} />
            </label>
            <label className="rpf-field">
              <span>Call-to-action label <em>(optional)</em></span>
              <input value={cfg.messaging.cta.label} disabled={disabled}
                     onChange={(e) => set("messaging.cta.label", e.target.value)} />
            </label>
            <label className="rpf-field">
              <span>Call-to-action link</span>
              <input value={cfg.messaging.cta.url} disabled={disabled}
                     onChange={(e) => set("messaging.cta.url", e.target.value)} />
            </label>
          </div>
        </details>
      </section>

      <section className="rpf-card">
        <h3>Embed code</h3>
        <p className="rpf-sub">
          Paste this once into the page that should show the store finder. Everything
          above is read at page load, so future changes here go live without touching
          the website again.
        </p>
        <pre className="rpf-embed">{embed}</pre>
        <button className="rpf-btn" onClick={copyEmbed} disabled={!publicKey}>
          {copied ? "Copied" : "Copy embed code"}
        </button>
      </section>

      {/* LAST on the tab, after the embed snippet (item 75). A sticky bottom bar
          only pins while its own slot is below the fold, so sitting mid-page it
          scrolled away exactly when someone reached the code they came for.
          Placed last, it stays reachable from anywhere on the tab. */}
      {canEdit && (
        <div className="rpf-card-actions rpf-sticky-actions">
          {saved && <span className="rpf-saved">Saved</span>}
          <button className="rpf-btn rpf-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save widget settings"}
          </button>
        </div>
      )}
    </div>
  );
}
